import React, { useEffect, useState } from 'react';
import { Menu, Button, Typography, Modal, message, Divider, Tooltip, Switch } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import {
  DashboardOutlined,
  ShoppingOutlined,
  UserOutlined,
  FileTextOutlined,
  TeamOutlined,
  CloseOutlined,
  GiftOutlined,
  EnvironmentOutlined,
  SyncOutlined,
  DatabaseOutlined,
  CreditCardOutlined,
  ShopOutlined,
  SettingOutlined,
  SafetyOutlined,
  KeyOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  TagsOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideModalProps } from '../../types/types';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import Logo from '../Logo';
import './style.scss';

const { Title, Text } = Typography;

interface SyncDataItem {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const getSubmenuOpenKeys = (pathname: string): string[] => {
  if (pathname.startsWith('/payment-methods') || pathname.startsWith('/customer-types')) {
    return ['/settings'];
  }
  if (pathname.startsWith('/reports')) {
    return ['/reports'];
  }
  if (
    pathname.startsWith('/users') ||
    pathname.startsWith('/roles') ||
    pathname.startsWith('/permissions') ||
    pathname.startsWith('/models')
  ) {
    return ['/system-management'];
  }
  return [];
};

const SUBMENU_POPUP_CLASS = 'premium-sidebar-submenu-popup';
const MENU_TOOLTIP_CLASS = 'premium-sidebar-menu-tooltip';

const getMenuLabelText = (label: React.ReactNode): string | undefined =>
  typeof label === 'string' ? label : undefined;

/** Popup class for submenus + hover labels when the sidebar is collapsed. */
const prepareMenuItems = (
  items: ItemType[],
  options: { collapsed: boolean; tooltipPlacement: 'left' | 'right' }
): ItemType[] =>
  items.map((item): ItemType => {
    if (!item) return item;

    const label = 'label' in item ? item.label : undefined;
    const labelText = getMenuLabelText(label);
    const hasChildren =
      'children' in item && Array.isArray(item.children) && item.children.length > 0;
    const icon = 'icon' in item ? item.icon : undefined;
    const prepared: ItemType = { ...item };

    if (hasChildren) {
      (prepared as ItemType & { popupClassName?: string }).popupClassName = SUBMENU_POPUP_CLASS;
    }

    if (options.collapsed && labelText) {
      if (icon) {
        (prepared as ItemType & { icon?: React.ReactNode }).icon = (
          <Tooltip
            title={labelText}
            placement={options.tooltipPlacement}
            mouseEnterDelay={0.15}
            classNames={{ root: MENU_TOOLTIP_CLASS }}
          >
            <span className="premium-sidebar-icon-tooltip-target">{icon}</span>
          </Tooltip>
        );
        // Ant Design accepts `false` at runtime to suppress the default collapsed tooltip.
        (prepared as ItemType & { title?: string | false }).title = false;
      } else {
        (prepared as ItemType & { title?: string }).title = labelText;
      }
    }

    return prepared;
  });

const SideModal: React.FC<SideModalProps> = ({
  isOpen = false,
  onClose,
  embedded = false,
  collapsed = false,
  onCollapsedChange,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { hasPermission, hasRole, canAccessMenuRoute } = usePermissions();
  const { t, i18n } = useTranslation();
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncingData, setSyncingData] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>(() => getSubmenuOpenKeys(location.pathname));

  // Debug: Log current language and translations
  useEffect(() => {
    // Language and translations are handled automatically by i18n
  }, [i18n.language, t]);

  const syncDataItems: SyncDataItem[] = [
    {
      key: 'customers',
      label: t('syncData.customers'),
      icon: <UserOutlined />,
    },
    {
      key: 'categories',
      label: t('syncData.categories'),
      icon: <ShoppingOutlined />,
    },
    {
      key: 'product_unit_types',
      label: t('syncData.productUnitTypes'),
      icon: <DatabaseOutlined />,
    },
    {
      key: 'accounts',
      label: t('syncData.accounts'),
      icon: <DatabaseOutlined />,
    },
    {
      key: 'inventories',
      label: t('syncData.inventories'),
      icon: <DatabaseOutlined />,
    },
    {
      key: 'products',
      label: t('syncData.products'),
      icon: <ShoppingOutlined />,
    },
    {
      key: 'invoices',
      label: t('syncData.invoices'),
      icon: <FileTextOutlined />,
    },
    {
      key: 'receipts',
      label: t('syncData.receipts'),
      icon: <FileTextOutlined />,
    },
  ];

  const handleSyncData = async (dataType: string) => {
    setSyncingData(dataType);
    try {
      const response: any = await Api.get(`/api/admin/fetch?data=${dataType}`);

      if (response.status) {
        message.success(t('syncData.success', { dataType: t(`syncData.${dataType}`) }));
      } else {
        message.error(
          response.message || t('syncData.error', { dataType: t(`syncData.${dataType}`) })
        );
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      message.error(t('syncData.error', { dataType: t(`syncData.${dataType}`) }));
    } finally {
      setSyncingData(null);
    }
  };

  // Check if user is Super Admin or Administrator
  const isSuperAdmin = hasRole('superadministrator');
  const isAdministrator = hasRole('administrator');

  // Helper function to check if user has permission (for showing menu items)
  const canAccess = (permission?: string | null): boolean => {
    if (isSuperAdmin) return true;
    if (!permission) return false;
    return hasPermission(permission);
  };

  // Helper function to check if user has ANY of the given permissions
  const canAccessAny = (permissions: string[]): boolean => {
    if (isSuperAdmin) return true;
    return permissions.some((perm) => hasPermission(perm));
  };

  // Build menu items dynamically based on permissions
  // Items are HIDDEN (not shown) if user doesn't have permission
  // Super Admin sees everything
  const menuItems = [
    // Dashboard - requires view_dashboard permission
    canAccess('view_dashboard')
      ? {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: t('navigation.dashboard'),
        }
      : null,
    // Customers - requires any customer-related permission
    canAccessAny(['view_customers', 'view_customer', 'create_customer', 'update_customer'])
      ? {
          key: '/customers',
          icon: <UserOutlined />,
          label: t('navigation.customers'),
        }
      : null,
    // Products - requires any product-related permission
    canAccessAny(['view_products', 'view_product', 'create_product', 'update_product'])
      ? {
          key: '/products',
          icon: <ShoppingOutlined />,
          label: t('navigation.products'),
        }
      : null,
    // Sales Representatives - requires any sales rep permission
    canAccessAny([
      'view_sales_representatives',
      'view_sales_representative',
      'create_sales_representative',
      'update_sales_representative',
    ])
      ? {
          key: '/sales-representatives',
          icon: <TeamOutlined />,
          label: t('navigation.salesRepresentatives'),
        }
      : null,
    // Orders - requires any order-related permission
    canAccessAny(['view_orders', 'view_order', 'create_order', 'update_order'])
      ? {
          key: '/orders',
          icon: <FileTextOutlined />,
          label: t('navigation.orders'),
        }
      : null,
    // Visits - requires any visit-related permission
    canAccessAny(['view_visits', 'view_visit', 'update_visit', 'download_visit_report'])
      ? {
          key: '/visits',
          icon: <EnvironmentOutlined />,
          label: t('navigation.visits'),
        }
      : null,
    (() => {
      // Sidebar: menu model on role + gate permission (not dashboard chart permissions)
      const reportChildren = [
        canAccessMenuRoute('/reports/sales', ['sales_report.view', 'sales_report.download'])
          ? {
              key: '/reports/sales',
              icon: <FileTextOutlined />,
              label: t('reports.tabs.sales', 'Sales report'),
            }
          : null,
        canAccessMenuRoute('/reports/sales-analytics', [
          'sales_analytics.view',
          'sales_analytics.download',
        ])
          ? {
              key: '/reports/sales-analytics',
              icon: <FileTextOutlined />,
              label: t('reports.tabs.analytics', 'Sales analytics'),
            }
          : null,
      ].filter(Boolean) as { key: string; icon: React.ReactNode; label: string }[];

      // Require assigned menu model per child; parent when at least one child is visible
      return reportChildren.length > 0
        ? {
            key: '/reports',
            icon: <FileTextOutlined />,
            label: t('navigation.reports'),
            className: 'menu-item--reports',
            children: reportChildren,
          }
        : null;
    })(),
    // Loyalty Points - requires module-level loyalty points permission
    canAccessAny(['view_loyalty_points', 'loyalty_points.read'])
      ? {
          key: '/loyalty-points',
          icon: <GiftOutlined />,
          label: t('navigation.loyaltyPoints'),
        }
      : null,
    // Offers - requires any offers permission
    canAccessAny([
      'view_offers',
      'view_offer',
      'offers.read',
      'create_offer',
      'offers.create',
      'update_offer',
      'offers.update',
    ])
      ? {
          key: '/offers',
          icon: <TagsOutlined />,
          label: t('navigation.offers'),
        }
      : null,
    // Settings - show if user has access to any child item
    (() => {
      const settingsChildren = [
        canAccessAny([
          'manage_payment_methods',
          'payment_methods.read',
          'payment_methods.create',
          'payment_methods.update',
        ])
          ? {
              key: '/payment-methods',
              icon: <CreditCardOutlined />,
              label: t('navigation.paymentMethods'),
            }
          : null,
        canAccessAny([
          'manage_customer_types',
          'customer_types.read',
          'customer_types.create',
          'customer_types.update',
        ])
          ? {
              key: '/customer-types',
              icon: <ShopOutlined />,
              label: t('navigation.customerTypes'),
            }
          : null,
        canAccessAny(['update_customer', 'create_customer'])
          ? {
              key: '/account-agreement',
              icon: <FileTextOutlined />,
              label: t('navigation.accountAgreement', 'Account agreement'),
            }
          : null,
      ].filter(Boolean);

      // Only show Settings parent if there's at least one accessible child
      return settingsChildren.length > 0
        ? {
            key: '/settings',
            icon: <SettingOutlined />,
            label: t('navigation.settings'),
            children: settingsChildren,
          }
        : null;
    })(),
    // System Management - show if user has access to any child item
    (() => {
      const systemManagementChildren = [
        canAccessAny(['users.view', 'users.create', 'users.update', 'users.delete'])
          ? {
              key: '/users',
              icon: <UsergroupAddOutlined />,
              label: t('navigation.users'),
            }
          : null,
        canAccessAny(['models.view', 'models.create', 'models.update', 'models.delete'])
          ? {
              key: '/models',
              icon: <AppstoreOutlined />,
              label: t('navigation.models'),
            }
          : null,
        canAccessAny(['roles.view', 'roles.create', 'roles.update', 'roles.delete'])
          ? {
              key: '/roles',
              icon: <SafetyOutlined />,
              label: t('navigation.roles'),
            }
          : null,
        canAccessAny([
          'permissions.view',
          'permissions.create',
          'permissions.update',
          'permissions.delete',
        ])
          ? {
              key: '/permissions',
              icon: <KeyOutlined />,
              label: t('navigation.permissions'),
            }
          : null,
      ].filter(Boolean);

      // Only show System Management parent if there's at least one accessible child
      return systemManagementChildren.length > 0
        ? {
            key: '/system-management',
            icon: <SettingOutlined />,
            label: t('navigation.systemManagement'),
            children: systemManagementChildren,
          }
        : null;
    })(),
  ].filter(Boolean) as ItemType[];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'sync-data') {
      setSyncModalVisible(true);
    } else if (key && key.startsWith('/')) {
      history.push(key);
      // Close floating submenu after navigation in collapsed rail
      if (collapsed && embedded) {
        setOpenKeys([]);
      }
      if (!embedded && onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    const routeKeys = getSubmenuOpenKeys(location.pathname);
    if (routeKeys.length === 0) return;

    setOpenKeys((prev) => Array.from(new Set([...prev, ...routeKeys])));
  }, [location.pathname]);

  const sidebarTooltipPlacement = i18n.language?.startsWith('ar') ? 'left' : 'right';

  const renderNavMenu = () => (
    <Menu
      mode="inline"
      className="premium-sidebar-menu"
      selectedKeys={[location.pathname]}
      openKeys={openKeys}
      onOpenChange={setOpenKeys}
      inlineCollapsed={collapsed}
      triggerSubMenuAction={collapsed ? 'click' : 'hover'}
      getPopupContainer={() => document.body}
      items={prepareMenuItems(menuItems, {
        collapsed: collapsed && embedded,
        tooltipPlacement: sidebarTooltipPlacement,
      })}
      onClick={handleMenuClick}
    />
  );

  // Desktop sidebar content (inside fixed Sider)
  if (embedded) {
    return (
      <div className={`sidebar-content${collapsed ? ' sidebar-content--collapsed' : ''}`}>
        {/* Sidebar Header with Logo */}
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="brand-logo-wrap">
              <Logo size="small" invert style={{ width: collapsed ? 32 : 36, height: 'auto' }} />
            </div>
            {!collapsed && (
              <Title level={4} className="brand-text">
                DES Solutions
              </Title>
            )}
          </div>
          {!collapsed && <Text className="brand-subtitle">{t('branding.companyName')}</Text>}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-nav" aria-label={t('navigation.toggleMenu', 'Navigation')}>
          {renderNavMenu()}
        </nav>

        {/* Sync Data Section - Only show for Super Admin and Administrator */}
        {(isSuperAdmin || isAdministrator) && (
          <>
            {!collapsed && (
              <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
            )}
            <div className="sync-data-section">
              {!collapsed && (
                <div className="sync-data-header">
                  <SyncOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  <Text style={{ color: 'white', fontWeight: 600 }}>{t('syncData.title')}</Text>
                </div>
              )}
              <Tooltip
                title={collapsed ? t('syncData.syncButton') : undefined}
                placement={i18n.language?.startsWith('ar') ? 'left' : 'right'}
              >
                <Button
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncModalVisible(true)}
                  className="sync-data-trigger"
                  aria-label={t('syncData.syncButton')}
                >
                  {!collapsed && t('syncData.syncButton')}
                </Button>
              </Tooltip>
            </div>
          </>
        )}

        {onCollapsedChange && (
          <div className="sidebar-preference-section">
            {!collapsed ? (
              <div className="sidebar-preference-row">
                <MenuUnfoldOutlined className="sidebar-preference-row__icon" aria-hidden />
                <Text className="sidebar-preference-row__label">
                  {t('navigation.keepSidebarCollapsed')}
                </Text>
                <Switch
                  size="small"
                  checked={collapsed}
                  onChange={onCollapsedChange}
                  aria-label={t('navigation.keepSidebarCollapsed')}
                />
              </div>
            ) : (
              <Tooltip
                title={t('navigation.keepSidebarCollapsed')}
                placement={i18n.language?.startsWith('ar') ? 'left' : 'right'}
              >
                <div className="sidebar-preference-row sidebar-preference-row--collapsed">
                  <MenuFoldOutlined className="sidebar-preference-row__icon" aria-hidden />
                  <Switch
                    size="small"
                    checked={collapsed}
                    onChange={onCollapsedChange}
                    aria-label={t('navigation.keepSidebarCollapsed')}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        )}

        {/* Sync Data Modal */}
        <Modal
          // title={t('syncData.title')}
          visible={syncModalVisible}
          onCancel={() => setSyncModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setSyncModalVisible(false)}>
              {t('common.cancel')}
            </Button>,
            // <Button
            //   key="sync-all"
            //   type="primary"
            //   icon={<SyncOutlined />}
            //   loading={syncingData === 'all'}
            //   onClick={handleSyncAllData}
            // >
            //   {t('syncData.syncAll')}
            // </Button>,
          ]}
          width={600}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('syncData.description')}</Text>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 8,
            }}
          >
            {syncDataItems.map((item) => (
              <Button
                key={item.key}
                icon={item.icon}
                loading={syncingData === item.key}
                onClick={() => handleSyncData(item.key)}
                style={{ height: 'auto', padding: '12px 16px', textAlign: 'left' }}
                block
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>{item.key}</div>
                </div>
              </Button>
            ))}
          </div>
        </Modal>
      </div>
    );
  }

  // Mobile sidebar with overlay
  return (
    <div className="side-modal">
      {/* Mobile overlay */}
      <div className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />

      <div className={`responsive-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Mobile close button */}
        <div className="mobile-close-button">
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
        </div>

        <div className="sidebar-content">
          {/* Sidebar Header with Logo */}
          <div className="sidebar-header">
            <div className="logo-container">
              <div className="brand-logo-wrap">
                <Logo size="small" invert style={{ width: 36, height: 'auto' }} />
              </div>
              <Title level={4} className="brand-text">
                DES Solutions
              </Title>
            </div>
            <Text className="brand-subtitle">{t('branding.companyName')}</Text>
          </div>

          {/* Navigation Menu */}
          <nav className="sidebar-nav" aria-label={t('navigation.toggleMenu', 'Navigation')}>
            {renderNavMenu()}
          </nav>

          {/* Sync Data Section - Only show for Super Admin and Administrator */}
          {(isSuperAdmin || isAdministrator) && (
            <>
              <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />
              <div className="sync-data-section">
                <Button
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncModalVisible(true)}
                  style={{
                    color: 'white',
                    width: '100%',
                    textAlign: 'left',
                    height: 'auto',
                    padding: '8px 16px',
                  }}
                >
                  {t('syncData.syncButton')}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Sync Data Modal */}
        <Modal
          // title={t('syncData.title')}
          visible={syncModalVisible}
          onCancel={() => setSyncModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setSyncModalVisible(false)}>
              {t('common.cancel')}
            </Button>,
            // <Button
            //   key="sync-all"
            //   type="primary"
            //   icon={<SyncOutlined />}
            //   loading={syncingData === 'all'}
            //   onClick={handleSyncAllData}
            // >
            //   {t('syncData.syncAll')}
            // </Button>,
          ]}
          width={600}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('syncData.description')}</Text>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 8,
            }}
          >
            {syncDataItems.map((item) => (
              <Button
                key={item.key}
                icon={item.icon}
                loading={syncingData === item.key}
                onClick={() => handleSyncData(item.key)}
                style={{ height: 'auto', padding: '12px 16px', textAlign: 'left' }}
                block
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>{item.key}</div>
                </div>
              </Button>
            ))}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SideModal;
